import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../../common/constants';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Avatar, Tag, Grid } from 'antd';
import {
    BellOutlined,
    UserOutlined,
    LogoutOutlined,
    MenuOutlined,
    CustomerServiceOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { demandeAssistancesService } from '../../../common/services';
import { io } from 'socket.io-client';
import { useLayout } from '../LayoutContext';
import { getPageConfig } from '../../../common/config/pageConfig';

const socket = io('https://api.tigoon-connect.com/api/v1');

const Navbar = () => {
    const auth = useContext(AuthContext);
    const { currentUser } = auth;
    const { toggleSidebar, isXLUp } = useLayout();
    const navigate = useNavigate();
    const location = useLocation();

    const [pendingCount, setPendingCount] = useState(0);
    const [pendingDemandes, setPendingDemandes] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const notifDropdownRef = useRef(null);
    const previousCountRef = useRef(0);

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    // Obtenir la configuration de la page actuelle
    const pageConfig = getPageConfig(location.pathname);
    const PageIcon = pageConfig.icon;

    // Helper pour extraire un tableau
    const toArray = (res) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.items)) return res.items;
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.items)) return res.data.items;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        if (Array.isArray(res?.results)) return res.results;
        if (Array.isArray(res?.payload)) return res.payload;
        return [];
    };

    // Formater la date relative
    const formatTimeAgo = (dateString) => {
        if (!dateString) return '';
        // Gérer le format DD/MM/YYYY HH:mm:ss retourné par l'API
        let date;
        const ddmmyyyy = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):?(\d{2})?$/);
        if (ddmmyyyy) {
            const [, day, month, year, hour, min, sec] = ddmmyyyy;
            date = new Date(year, month - 1, day, hour, min, sec || 0);
        } else {
            date = new Date(dateString);
        }
        if (isNaN(date.getTime())) return dateString;
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "À l'instant";
        if (diffMins < 60) return `il y a ${diffMins} min`;
        if (diffHours < 24) return `il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
        if (diffDays < 7) return `il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
        return date.toLocaleDateString('fr-FR');
    };

    // Formater la date du jour
    const formatCurrentDate = () => {
        return new Date().toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Fermer les dropdowns si on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
            if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target)) {
                setShowNotifDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const checkNewDemandes = async () => {
            try {
                const res = await demandeAssistancesService.get();
                const allDemandes = toArray(res);
                const enAttente = allDemandes.filter(d => d?.statut === 'en_attente');

                if (enAttente.length > previousCountRef.current && previousCountRef.current > 0) {
                    const nouvelles = enAttente.length - previousCountRef.current;
                    toast.info(`${nouvelles} nouvelle(s) demande(s) d'assistance !`, {
                        position: "top-right",
                        autoClose: 5000,
                    });
                }

                setPendingCount(enAttente.length);
                setPendingDemandes(enAttente.slice(0, 5));
                previousCountRef.current = enAttente.length;
            } catch (err) {
                console.error('Erreur lors du contrôle des demandes :', err);
            }
        };

        checkNewDemandes();
        const interval = setInterval(checkNewDemandes, 15000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const onNouvelle = async () => {
            try {
                const res = await demandeAssistancesService.get();
                const allDemandes = toArray(res);
                const enAttente = allDemandes.filter(d => d?.statut === 'en_attente');
                setPendingCount(enAttente.length);
                setPendingDemandes(enAttente.slice(0, 5));
                toast.info("Nouvelle demande d'assistance !");
            } catch (e) {
                console.error(e);
            }
        };

        socket.on('nouvelle-demande', onNouvelle);
        return () => socket.off('nouvelle-demande', onNouvelle);
    }, []);

    useEffect(() => {
        const onUpdated = async () => {
            try {
                const res = await demandeAssistancesService.get();
                const allDemandes = toArray(res);
                const enAttente = allDemandes.filter(d => d?.statut === 'en_attente');
                setPendingCount(enAttente.length);
                setPendingDemandes(enAttente.slice(0, 5));
            } catch (e) {
                console.error(e);
            }
        };

        socket.on('demande_assistance_updated', onUpdated);
        return () => socket.off('demande_assistance_updated', onUpdated);
    }, []);

    const handleLogout = (e) => {
        e.preventDefault();
        setShowDropdown(false);
        toast.success("Déconnexion réussie", { position: "top-right", autoClose: 2000 });
        setTimeout(() => auth.onLogout(), 1000);
    };

    const getUserInitials = () => {
        if (!currentUser) return '';
        return `${currentUser.firstName?.[0] || ''}${currentUser.lastName?.[0] || ''}`.toUpperCase();
    };

    const getUserRole = () => {
        if (!currentUser) return '';
        return currentUser.role === 'superAdmin' ? 'Super Administrateur' : 'Administrateur';
    };

    const handleViewDemande = (demande) => {
        setShowNotifDropdown(false);
        navigate('/dashboard/demandes-assistances', { state: { selectedId: demande._id } });
    };

    const handleViewAllDemandes = () => {
        setShowNotifDropdown(false);
        navigate('/dashboard/demandes-assistances');
    };

    return (
        <nav
            className="layout-navbar"
            id="layout-navbar"
            style={{
                background: '#ffffff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                borderRadius: '0 0 16px 16px',
                padding: isMobile ? '12px 16px' : '16px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: isMobile ? 70 : 80
            }}
        >
            {/* Left: Burger + Page Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, flex: 1 }}>
                {/* Burger button (mobile) */}
                {!isXLUp && (
                    <button
                        className="navbar-burger"
                        type="button"
                        aria-label="Ouvrir le menu"
                        onClick={toggleSidebar}
                        style={{
                            background: '#f5f5f5',
                            border: 'none',
                            padding: '10px',
                            cursor: 'pointer',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <MenuOutlined style={{ fontSize: '1.2rem', color: '#595959' }} />
                    </button>
                )}

                {/* Page Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 16 }}>
                    <Avatar
                        size={isMobile ? 40 : 52}
                        style={{
                            background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                        }}
                        icon={<PageIcon style={{ fontSize: isMobile ? 20 : 26, color: 'white' }} />}
                    />
                    <div>
                        <h2 style={{
                            color: '#262626',
                            margin: 0,
                            fontSize: isMobile ? 16 : 20,
                            fontWeight: 600,
                            lineHeight: 1.2
                        }}>
                            {pageConfig.title}
                        </h2>
                        {!isMobile && (
                            <p style={{
                                color: '#8c8c8c',
                                margin: '2px 0 0 0',
                                fontSize: 13
                            }}>
                                {pageConfig.description}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Right side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
                {/* Date (desktop only) */}
                {!isMobile && (
                    <Tag
                        style={{
                            background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                            border: 'none',
                            color: 'white',
                            borderRadius: 8,
                            padding: '6px 12px',
                            fontSize: 13
                        }}
                    >
                        <CalendarOutlined style={{ marginRight: 6 }} />
                        {formatCurrentDate()}
                    </Tag>
                )}

                {/* Notifications */}
                <div ref={notifDropdownRef} style={{ position: 'relative' }}>
                    <Badge
                        count={pendingCount}
                        showZero
                        overflowCount={99}
                        offset={[-2, 2]}
                        style={{
                            backgroundColor: pendingCount > 0 ? '#f5222d' : '#d9d9d9',
                            fontSize: '11px',
                            minWidth: '18px',
                            height: '18px',
                            lineHeight: '18px'
                        }}
                    >
                        <button
                            className="notification-btn"
                            onClick={() => setShowNotifDropdown(!showNotifDropdown)}
                            style={{
                                background: '#f5f5f5',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '10px',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <BellOutlined style={{ fontSize: '1.2rem', color: '#595959' }} />
                        </button>
                    </Badge>

                    {/* Notification Dropdown */}
                    {showNotifDropdown && (
                        <div
                            className="notification-dropdown"
                            style={{
                                position: 'absolute',
                                top: 'calc(100% + 10px)',
                                right: 0,
                                background: '#fff',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                width: '360px',
                                maxWidth: '90vw',
                                zIndex: 1060,
                                overflow: 'hidden'
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    background: 'linear-gradient(135deg, #E8A849, #D4A54C)',
                                    padding: '15px 20px',
                                    color: '#fff',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                                        Demandes récentes
                                    </div>
                                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                                        {pendingCount} demande(s) en attente
                                    </div>
                                </div>
                                <div
                                    style={{
                                        background: '#fff',
                                        color: '#D4A54C',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontWeight: 600,
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    {pendingCount}
                                </div>
                            </div>

                            {/* Liste des demandes */}
                            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                {pendingDemandes.length > 0 ? (
                                    pendingDemandes.map((demande, index) => (
                                        <div
                                            key={demande._id || index}
                                            style={{
                                                padding: '12px 15px',
                                                borderBottom: '1px solid #F0F0F0',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '12px',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FAFAFA'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            onClick={() => handleViewDemande(demande)}
                                        >
                                            <div
                                                style={{
                                                    width: '36px',
                                                    height: '36px',
                                                    borderRadius: '8px',
                                                    background: 'rgba(212, 165, 76, 0.15)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <CustomerServiceOutlined style={{ color: '#D4A54C', fontSize: '1.1rem' }} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                                    <span style={{ fontWeight: 600, color: '#D4A54C', fontSize: '0.9rem' }}>
                                                        #{demande.id?.slice(-8).toUpperCase() || 'N/A'}
                                                    </span>
                                                    <span
                                                        style={{
                                                            background: 'rgba(232, 168, 73, 0.15)',
                                                            color: '#D4A54C',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            border: '1px solid #E8A849'
                                                        }}
                                                    >
                                                        En attente
                                                    </span>
                                                </div>
                                                <div style={{ color: '#333', fontSize: '0.9rem', fontWeight: 500, marginBottom: '4px' }}>
                                                    {demande.utilisateur?.prenom || ''} {demande.utilisateur?.nom || 'Partenaire'}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#999', fontSize: '0.8rem' }}>
                                                    <ClockCircleOutlined style={{ fontSize: '0.75rem' }} />
                                                    {formatTimeAgo(demande.createdAt)}
                                                </div>
                                            </div>
                                            <button
                                                style={{
                                                    background: 'none',
                                                    border: '1px solid #E5E5E5',
                                                    borderRadius: '6px',
                                                    padding: '6px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDemande(demande);
                                                }}
                                            >
                                                <EyeOutlined style={{ color: '#666', fontSize: '1rem' }} />
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <div
                                        style={{
                                            padding: '30px 20px',
                                            textAlign: 'center',
                                            color: '#999'
                                        }}
                                    >
                                        <BellOutlined style={{ fontSize: '2rem', marginBottom: '10px', opacity: 0.5 }} />
                                        <div>Aucune demande en attente</div>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div
                                style={{
                                    padding: '12px',
                                    borderTop: '1px solid #F0F0F0',
                                    textAlign: 'center'
                                }}
                            >
                                <button
                                    onClick={handleViewAllDemandes}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#D4A54C',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    Voir toutes les demandes
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* User name (desktop only) */}
                {!isMobile && (
                    <span style={{
                        fontWeight: 600,
                        color: '#262626',
                        fontSize: 14
                    }}>
                        {currentUser ? `${currentUser.firstName} ${currentUser.lastName}`.toUpperCase() : ''}
                    </span>
                )}

                {/* User Profile Dropdown */}
                <div className="user-profile-wrapper" ref={dropdownRef} style={{ position: 'relative' }}>
                    <div
                        className="user-profile"
                        onClick={() => setShowDropdown(!showDropdown)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '50%',
                            background: '#f5f5f5'
                        }}
                    >
                        <div
                            className="user-avatar"
                            style={{
                                width: isMobile ? 36 : 40,
                                height: isMobile ? 36 : 40,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.9rem'
                            }}
                        >
                            {currentUser?.photoUrl ? (
                                <img
                                    src={currentUser.photoUrl}
                                    alt="avatar"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                    }}
                                />
                            ) : (
                                getUserInitials()
                            )}
                        </div>
                    </div>

                    {/* Dropdown Menu */}
                    {showDropdown && (
                        <div
                            className="user-dropdown"
                            style={{
                                position: 'absolute',
                                top: 'calc(100% + 10px)',
                                right: 0,
                                background: '#fff',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                minWidth: '220px',
                                zIndex: 1060,
                                overflow: 'hidden'
                            }}
                        >
                            {/* Header */}
                            <div
                                className="dropdown-header"
                                style={{
                                    padding: '15px',
                                    borderBottom: '1px solid #F0F0F0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}
                            >
                                <div
                                    style={{
                                        width: '45px',
                                        height: '45px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #E8A849, #D4A54C)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontWeight: 600
                                    }}
                                >
                                    {getUserInitials()}
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                                        {currentUser ? `${currentUser.firstName} ${currentUser.lastName}`.toUpperCase() : ''}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                        {getUserRole()}
                                    </div>
                                </div>
                            </div>

                            {/* Menu Items */}
                            <div style={{ padding: '8px 0' }}>
                                <Link
                                    to="/dashboard/profile"
                                    onClick={() => setShowDropdown(false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 15px',
                                        color: '#333',
                                        textDecoration: 'none',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#F8F9FA'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                    <UserOutlined style={{ fontSize: '1.1rem', color: '#666' }} />
                                    <span>Mon Profil</span>
                                </Link>

                                <a
                                    href="#"
                                    onClick={handleLogout}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 15px',
                                        color: '#E07D3B',
                                        textDecoration: 'none',
                                        transition: 'background 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(224, 125, 59, 0.1)'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                    <LogoutOutlined style={{ fontSize: '1.1rem', color: '#E07D3B' }} />
                                    <span>Se déconnecter</span>
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
